import { useMemo } from 'react';
import {
    InfiniteData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useSuperUser } from '../../api.context';
import {
    availableEventToEvent,
    eventAssetToEventAssetDto,
    eventStaffToEventStaffDto,
    getEventItemDtoToEvent,
} from './event.converters';
import {
    EventAsset,
    EventItemMinimal,
    EventNote,
    EventStaff,
    UseEventsLazyData,
} from './event.types';
import {
    type CancelEventRes1Dto,
    type CancelEventResDto,
    EventsService,
    GetEventRes1Dto,
    GetEventResDto,
    GetEventsResDto,
    GetEventsResItem0BookingsItem0Dto,
    GetEventsResItem0Dto,
    GetEventsResItem1Dto,
    PaxData,
    ProductType,
    UpdateEventAssetsReq1Dto,
    UpdateEventAssetsReqDto,
    UpdateEventStaffReq1Dto,
    UpdateEventStaffReqDto,
} from '../../generated';
import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
} from '../common.types';
import { convertItemsToActionItemsDto } from '../common.converters';
import { useAvailability } from '../availability/availability.hooks';

export const useDayProductEvents = (
    params: {
        day: number;
        eventId?: string;
        eventStart?: Date;
        capacityId?: string;
        productId: string;
        pax: PaxData;
    },
    enabled = true
) => {
    const { data, ...other } = useAvailability(
        {
            requestBody: {
                product: { value: params.productId, label: '' },
                dates: {
                    startDate: new Date(params.day),
                    endDate: new Date(params.day),
                },
                pax: params.pax,
                events: params.eventId
                    ? [
                          {
                              id: params.eventId,
                              exclude: true,
                          },
                      ]
                    : undefined,
            },
        },
        {
            enabled:
                enabled && !!params.productId && !!params.day && !!params.pax,
        }
    );

    const parsedData: EventItemMinimal[] | undefined = useMemo(() => {
        const events = data?.events?.[0]?.products?.[0].events
            .map(event =>
                event.key && data?.products[event.key]
                    ? availableEventToEvent(event, data?.products[event.key])
                    : undefined
            )
            .filter(event => !!event) as EventItemMinimal[];
        if (!params.eventId) {
            return events?.filter(
                event =>
                    event.start !== params.eventStart?.getTime() ||
                    event.capacityId !== params.capacityId
            );
        }

        return events;
    }, [data, params.eventStart, params.capacityId]);

    return {
        data: parsedData,
        ...other,
    };
};

export const useEventsLazyKey = 'useEventsLazyKey';

export const useEventsLazy = (
    params: UseEventsLazyData = {},
    options: CustomInfiniteQueryOptions<GetEventsResDto> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 5;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useEventsLazyKey, params],
        retry: false,
        queryFn: async ({ pageParam }) => {
            const response = await EventsService.getEvents({
                query: {
                    ...(params.action && { action: params.action }),
                    ...(params.at_start && { at_start: params.at_start }),
                    ...(params.at_end && { at_end: params.at_end }),
                    ...(params.by && { by: params.by }),
                    ...(params.types && { types: params.types }),
                    ...(params.statuses && { statuses: params.statuses }),
                    ...(params.startTs && { start_ts: params.startTs }),
                    ...(params.endTs && { end_ts: params.endTs }),
                    ...(params.text_search && {
                        text_search: params.text_search,
                    }),
                    ...(pageParam?.page !== undefined && {
                        page_number: pageParam.page || 0,
                    }),
                    ...(pageSize && { page_size: pageSize }),
                    ...(pageParam?.startAtId && {
                        start_at_id: pageParam.startAtId,
                    }),
                    bookings: true,
                },
            });

            if (!response.data) throw new Error('Failed to get events');

            return response.data;
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        cacheTime: 0,
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page =>
                          (page.items || []).map(getEventItemDtoToEvent)
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData,
        ...other,
    };
};

export const useUpdateEventSilent = () => {
    const queryClient = useQueryClient();
    const queriesData = queryClient.getQueriesData({
        queryKey: [useEventsLazyKey],
        exact: false,
    });

    return (
        eventId: string,
        callback: (b: GetEventsResItemDto) => GetEventsResItemDto
    ) => {
        queriesData.forEach(([queryKey]) => {
            queryClient.setQueryData<InfiniteData<GetEventsResDto>>(
                queryKey,
                events => {
                    if (!events) return undefined;
                    const pages = events.pages.map(page => {
                        const items =
                            page.items?.map(ev =>
                                ev.id === eventId ? callback(ev) : ev
                            ) || [];
                        return { ...events, items };
                    });
                    return { ...events, pages };
                }
            );
        });
    };
};

type GetEventsResItemDto = GetEventsResItem0Dto | GetEventsResItem1Dto;

export const useUpdateEventBooking = () => {
    const updateEvent = useUpdateEventSilent();

    return (
        eventId: string,
        bookingId: string,
        callback: (
            b: GetEventsResItem0BookingsItem0Dto
        ) => GetEventsResItem0BookingsItem0Dto
    ) => {
        updateEvent(eventId, event => {
            const bookings =
                event?.bookings.map(b =>
                    b.id === bookingId
                        ? callback(b as GetEventsResItem0BookingsItem0Dto)
                        : b
                ) || [];
            return {
                ...event,
                bookings_summary: {
                    ...event,
                    bookings,
                },
            };
        });
    };
};

export const useEventKey = 'useEventKey';
export const useEvent = (
    id?: string,
    options: CustomQueryOptions<GetEventResDto | GetEventRes1Dto> = {}
) => {
    const { data, ...other } = useQuery({
        queryKey: [useEventKey, id],
        queryFn: async () => {
            const response = await EventsService.getEvent({
                path: { event_id: id as string },
            });

            if (!response.data) throw new Error('Failed to get event');

            return response.data;
        },
        enabled: !!id,
        ...options,
    });

    const parsedData = useMemo(() => {
        return data ? getEventItemDtoToEvent(data) : undefined;
    }, [data]);

    return {
        data: parsedData,
        ...other,
    };
};

type EventDto =
    | GetEventsResItem0Dto
    | GetEventsResItem1Dto
    | GetEventResDto
    | GetEventRes1Dto;

const mergeItems = (
    item: EventDto,
    newItem: CancelEventResDto | CancelEventRes1Dto
    // @ts-ignore-next-line
): EventDto => ({
    ...item,
    ...newItem,
    // @ts-ignore-next-line
    bookings: !newItem.bookings?.length ? item.bookings : newItem.bookings,
});

export const useEventCache = () => {
    const queryClient = useQueryClient();

    const update = (
        id: string,
        newItem: CancelEventResDto | CancelEventRes1Dto
    ) => {
        queryClient
            .getQueriesData({
                queryKey: [useEventsLazyKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<InfiniteData<GetEventsResDto>>(
                    queryKey,
                    data => {
                        if (!data?.pages) return undefined;
                        const pages = data.pages.map(page => {
                            const items =
                                page.items?.map(item =>
                                    item.id === id
                                        ? (mergeItems(item, newItem) as
                                              | GetEventsResItem0Dto
                                              | GetEventsResItem1Dto)
                                        : item
                                ) || [];
                            return { ...page, items };
                        });
                        return { ...data, pages };
                    }
                );
            });
        queryClient
            .getQueriesData({
                queryKey: [useEventKey, id],
                exact: true,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<EventDto>(queryKey, data => {
                    return data
                        ? (mergeItems(data, newItem) as
                              | GetEventResDto
                              | GetEventRes1Dto)
                        : undefined;
                });
            });
    };

    return {
        update,
    };
};

export const useUpdateEventOperations = (
    oldAssets: EventAsset[],
    oldStaff: EventStaff[],
    productType: ProductType,
    oldCapacityId: string,
    options: CustomMutationOptions<
        {
            assets: EventAsset[];
            staff: EventStaff[];
            capacityId: string;
            eventId: string;
            syncPricing: boolean;
        },
        CancelEventResDto | CancelEventRes1Dto | null
    > = {}
) => {
    const { update } = useEventCache();

    return useMutation({
        mutationFn: async ({
            assets,
            staff,
            eventId,
            capacityId,
            syncPricing,
        }) => {
            const requestAssets = convertItemsToActionItemsDto(
                oldAssets.map(eventAssetToEventAssetDto),
                assets.map(eventAssetToEventAssetDto),
                'id'
            );
            let response: CancelEventResDto | CancelEventRes1Dto | null = null;

            if (capacityId && capacityId !== oldCapacityId) {
                const capacityResponse =
                    await EventsService.updateCapacityEvents({
                        path: { event_id: eventId, capacity_id: capacityId },
                        query: syncPricing
                            ? { sync_bookings: syncPricing }
                            : {},
                    });

                if (!capacityResponse.data)
                    throw new Error('Failed to update event capacity');

                response = capacityResponse.data;
            }

            if (requestAssets.length) {
                const assetsResponse = await EventsService.updateEventAssets({
                    path: { event_id: eventId },
                    body: {
                        items: requestAssets,
                        type: productType as unknown as
                            | UpdateEventAssetsReqDto['type']
                            | UpdateEventAssetsReq1Dto['type'],
                    },
                });

                if (!assetsResponse.data)
                    throw new Error('Failed to update event assets');

                response = assetsResponse.data;
            }

            const requestStaff = convertItemsToActionItemsDto(
                oldStaff.map(eventStaffToEventStaffDto),
                staff.map(eventStaffToEventStaffDto),
                'id'
            );

            if (requestStaff.length) {
                const staffResponse = await EventsService.updateEventStaff({
                    path: { event_id: eventId },
                    body: {
                        items: requestStaff,
                        type: productType as unknown as
                            | UpdateEventStaffReqDto['type']
                            | UpdateEventStaffReq1Dto['type'],
                    },
                });

                if (!staffResponse.data)
                    throw new Error('Failed to update event staff');

                response = staffResponse.data;
            }
            return response;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].eventId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useCancelEvent = (
    options?: CustomMutationOptions<
        {
            id: string;
            reason?: string;
        },
        CancelEventResDto | CancelEventRes1Dto
    >
) => {
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ id, reason }) => {
            const response = await EventsService.cancelEvent({
                path: { event_id: id },
                body: {
                    message: reason || '',
                },
                query: superUser ? { superuser: true } : {},
            });

            if (!response.data) throw new Error('Failed to cancel event');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO update event in cache
            options?.onSuccess?.(...args);
        },
    });
};

export const useLockEvent = (
    options?: CustomMutationOptions<
        {
            eventId: string;
            locked: boolean;
        },
        CancelEventResDto | CancelEventRes1Dto
    >
) => {
    const { update } = useEventCache();

    return useMutation({
        mutationFn: async ({ eventId, locked }) => {
            const response = await EventsService.updateLockEvents({
                path: { event_id: eventId },
                query: { locked },
            });

            if (!response.data) throw new Error('Failed to lock/unlock event');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].eventId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateEventNotes = (
    oldItems: EventNote[],
    options?: CustomMutationOptions<
        { eventId: string; notes: EventNote[] },
        CancelEventResDto | CancelEventRes1Dto | null
    >
) => {
    const { update } = useEventCache();

    return useMutation({
        mutationFn: async ({ eventId, notes }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems,
                notes,
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response = await EventsService.updateEventNotes({
                    path: { event_id: eventId },
                    body: {
                        items: requestItems,
                    },
                });

                if (!response.data)
                    throw new Error('Failed to update event notes');

                return response.data;
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].eventId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};
